import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { MetaChip } from "../../../../components/UIToolkit/Badges/MetaChip";
import { type DayToggleCell, DayToggleGrid, type DayToggleGroup, dayToggleStripWidth } from "../../../../components/UIToolkit/Clickable/DayToggleGrid";
import { InlineEditableText } from "../../../../components/UIToolkit/Inputs/InlineEditableText";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import {
	ACCENT_BLUE,
	BADGE,
	CONTROL_HEIGHT,
	FONT_SIZE,
	GRID_RADIUS,
	ICON_SIZE,
	LETTER_SPACING,
	MOTION,
	NEUTRAL,
	SUGGESTED_HIRE,
	SURFACE,
} from "../../../../theme/tokens";
import type { GridDates } from "../types";
import { type GapTone, gapToneStyle } from "../utils/gapTones";
import { dayDateOf, formatGridDayDate } from "../utils/gridDates";
import { cellKey, DAYS_PER_WEEK, type SuggestedHireDraft, shiftPillLabel, shiftsByWeek } from "../utils/suggestedHires";
import { columnToDayIdx, dayKeyAt } from "../utils/weekStart";

type Props = {
	hire: SuggestedHireDraft;
	/**
	 * The name to render. Passed in rather than read off the draft because it is DERIVED from the shape
	 * of the person's week for a generated row, and the numbering that disambiguates two "Weekend"s is a
	 * property of the whole list. A row the user renamed carries its own name through unchanged.
	 */
	displayName: string;
	gridWeeks: number;
	weekNumbers: number[];
	weekStartDay: number;
	/** REQUIRED, not optional: a silently date-less chip would hide a wiring bug rather than fail. */
	dates: GridDates;
	/** The department's template labels, chronological — the only shift types anyone can be hired into. */
	shiftLabels: string[];
	/** Tone of this cell's remaining gap, already filtered to THIS person's shift types. */
	markerToneFor: (key: number) => GapTone;
	/** Pinned: this row's cells stay highlighted in the gaps table until it's clicked again. */
	selected: boolean;
	/** Position in the list — drives the entrance stagger only. */
	index: number;
	onSelectToggle: () => void;
	onHoverChange: (hovered: boolean) => void;
	onRename: (name: string) => void;
	onShiftsChange: (labels: string[]) => void;
	/** `null` clears the day; a label assigns it. The row resolves which label before calling. */
	onDayChange: (key: number, label: string | null) => void;
	/** Multi-shift people need the shift chosen before an OFF day can be switched on. */
	onDayNeedsShift: (key: number) => void;
	/** The day that ask is open for, so its chip can show which one the picker means. `null` when closed. */
	pendingDayKey: number | null;
	/**
	 * Shift labels this row points at that the department no longer runs — see `retiredShiftsOf`.
	 *
	 * Non-empty means the row cannot be acted on: Apply would add coverage to a grid row nobody draws.
	 * It stays VISIBLE rather than being dropped, because a row you named disappearing with no reason
	 * given is its own kind of surprise — and the gap it was covering would go unexplained.
	 */
	retiredShifts: string[];
	onApplyToggle: () => void;
	onRemove: () => void;
	/** Opens the app's Recurring Shift modal, pre-filled from this person's proposed week. */
	onAddRecurring: () => void;
	/** The walkthrough anchor, placed on the row's root — see `_schema.md` → "The `anchor` contract". */
	"data-annie-target"?: string;
};

/**
 * The module's chip radius — `theme.shape.borderRadius` × `GRID_RADIUS.CHIP`, the app's existing
 * named "metadata chip" step, and the same value the panel's legend chips use.
 */
const CHIP_RADIUS = GRID_RADIUS.CHIP;

/** Anything the user can operate; a click that lands inside one must not also toggle the row's pin. */
const INTERACTIVE_SELECTOR = "button, input, a, [role='button'], [role='combobox'], [role='listbox']";

/**
 * The row's column template.
 *
 * Below `lg` the row stacks (one column); from `lg` up the three zones — identity, the day strip, the
 * actions — are pinned columns, which is what keeps every row's strip at an identical x down the list.
 * `dayStripWidth` comes from the same `DAY_TOGGLE` tokens the chips are drawn from.
 */
export const hireRowColumns = (dayStripWidth: number) => ({
	xs: "1fr",
	// Identity hugs its content, the day strip takes its pinned width immediately after it, and the
	// FLEXIBLE column is the trailing gap — so the strip sits left (right after the pills) while the
	// action cluster stays right-aligned at the end of the row. The strip's column is still a fixed
	// px width, which is what keeps every row's strip at an identical x.
	lg: `${SUGGESTED_HIRE.IDENTITY_WIDTH}px ${dayStripWidth}px minmax(${SUGGESTED_HIRE.ACTIONS_WIDTH}px, 1fr)`,
});

/** Cell keys and captions for one hire's week strip. */
export const buildDayGroups = (
	hire: SuggestedHireDraft,
	gridWeeks: number,
	weekNumbers: number[],
	weekStartDay: number,
	/** The grid's own dates, so a day chip can name the calendar day it stands for. */
	dates: GridDates,
	markerToneFor: (key: number) => GapTone,
	t: (key: string, options?: Record<string, unknown>) => string,
	/** The day whose shift the picker below the row is currently asking about, if any. */
	pendingKey: number | null,
	/** The row points at a shift the department no longer runs, so its week cannot be edited. */
	retired: boolean,
): DayToggleGroup[] =>
	// No cluster caption: the gap between the two 7-day blocks already separates the weeks, and a
	// caption repeated down a 17-row list was noise the design doesn't have. The week still reaches
	// assistive tech — it's in every chip's `ariaLabel`.
	Array.from({ length: gridWeeks }, (_, weekIdx) => ({
		key: weekIdx,
		cells: Array.from({ length: DAYS_PER_WEEK }, (_, column): DayToggleCell => {
			const dayIdx = columnToDayIdx(column, weekStartDay);
			const dayKey = dayKeyAt(column, weekStartDay);
			const key = cellKey(weekIdx, dayIdx);
			const dayName = t(`hiring.day.${dayKey}`);
			// The SAME field the legend swatch above the list reads, so a marker can never be a different
			// colour from the tier it stands for. An ungraded day has no marker value at all.
			const { marker, ink } = gapToneStyle(markerToneFor(key));
			return {
				key,
				label: dayName.charAt(0),
				selected: hire.shiftByDay[key] !== undefined,
				ariaLabel: t("hiring.hire.row.dayToggle", {
					day: dayName,
					date: formatGridDayDate(dayDateOf(dates, weekIdx, dayIdx, weekStartDay)),
					week: weekNumbers[weekIdx],
				}),
				// The tier's dark ink rings its saturated dot: the yellow marker is deliberately light, so
				// the default white ring would leave it edgeless on the unselected (pale) chips.
				marker: marker === undefined ? undefined : { color: marker, ringColor: ink, tooltip: t("hiring.hire.row.stillShort") },
				/**
				 * The day the shift picker is open for, held in the accent as a PENDING state.
				 *
				 * A `tone` and not `selected: true`: the day is not assigned yet — that is the question
				 * being asked — and borrowing the ON fill would claim it was. `tone` also applies only
				 * while unselected, which is exactly when this can happen, so it cannot collide with a
				 * real selection. Without it the picker named a day in prose ("Which shift on Fri, week
				 * 2?") while every chip in the row looked untouched.
				 */
				tone: key === pendingKey ? { bg: ACCENT_BLUE.TINT, ink: ACCENT_BLUE.INK, border: ACCENT_BLUE.MAIN } : undefined,
				disabled: retired,
				dataTestId: `hiring-hire-day-${hire.id}-${key}`,
			};
		}),
	}));

/**
 * The row's descriptive pills read as small caps — they are labels ("CNA", "MORNING + NIGHT", "8H",
 * "4-4 PER WK"), not sentences, and the uppercase run is what keeps them from competing with the name.
 *
 * `nowrap` + `flexShrink: 0` are load-bearing, not polish: a pill is a fixed label that either fits or
 * is wrong, and with the flex default of `shrink: 1` the run compressed to the column and every one of
 * them truncated ("NUR…", "8…", "1-2 PER …") or broke across two lines ("12AM-" / "8AM"), which also
 * made the row taller than `ROW_MIN_HEIGHT`. `SUGGESTED_HIRE.IDENTITY_WIDTH` is budgeted from these
 * pills' measured widths, so the correct behaviour when the column is short is to be visibly short —
 * not to shave every label.
 */
const UPPERCASE_CHIP_SX = {
	textTransform: "uppercase",
	letterSpacing: LETTER_SPACING.LABEL,
	fontSize: FONT_SIZE.LABEL_XS,
	whiteSpace: "nowrap",
	flexShrink: 0,
	// RECTANGULAR, matching the legend chips in `SuggestedHiresPanel` — the whole module speaks one
	// shape now. A pill reads as something you can tap or dismiss, and these only ever state a fact.
	borderRadius: CHIP_RADIUS,
} as const;

/**
 * The shift pill: an uppercase chip in the `info` tone that happens to open a picker. Built on the
 * dropdown's own pill trigger rather than a `MetaChip` so editing which shifts a person covers stays
 * one click on the thing that names them.
 *
 * FLAT, not boxed. It sits in a run of four chips that all describe the same person, and a bordered
 * control with a permanent caret made one of them look like a form field — it read as the loudest
 * thing on the line and cost the width that kept the row from fitting on one. So the box, the border
 * and the resting caret all go, and the tint (matching the `MetaChip` tone beside it) is what says
 * "this is a chip"; the caret returns on hover and on keyboard focus to say "…and it opens".
 *
 * NO CARET. The trigger always renders one as a flex child, and hiding it with `opacity: 0` (which is
 * what this used to do) keeps its ~14px box plus the flex gap — so the rectangle ended well past the
 * end of "12AM-8AM". `display: none` is what actually takes it out of layout; the caret cannot instead
 * be revealed on hover, because appearing would then widen the pill and shove the three chips beside it
 * along, and this row is hovered constantly (hovering it highlights its cells in the grid below).
 *
 * What is lost is a resting affordance, and it was already lost: the caret was hidden at rest on every
 * hover-capable pointer. The tint, the pointer cursor and the hover darkening carry it, and the trigger
 * keeps its `role="combobox"` / `aria-expanded`, so nothing changes for a screen reader.
 *
 * `selectSx` lands last in the trigger's `sx` array, so this descendant selector out-specifies the
 * caret's own emotion class.
 */
const shiftPillSx = (theme: Theme) =>
	({
		textTransform: "uppercase",
		letterSpacing: LETTER_SPACING.LABEL,
		fontSize: FONT_SIZE.LABEL_XS,
		fontWeight: 600,
		minHeight: BADGE.STATUS_CHIP_HEIGHT_SM,
		// Same contract as the `MetaChip`s beside it (see `UPPERCASE_CHIP_SX`): a shift label is
		// "12AM-8AM" or it is nothing. Wrapping it was the worst symptom of the old width budget — the
		// pill broke into "12AM-" / "8AM" and took the row's height with it — and it is the pill
		// `IDENTITY_WIDTH` is anchored on, which now needs ~16px less of it than that budget assumed.
		whiteSpace: "nowrap",
		// The one flexible item in the run. Every chip beside it is `flexShrink: 0` because a JOB, an
		// `8H` or a `3-4 PER WK` is meaningless half-rendered; a shift label still reads when it is
		// clipped. `shiftPillLabel` collapses the selection to one label plus `+N`, so this is a
		// backstop, not the mechanism — and it has to clip on BOTH boxes: `selectSx` lands on the
		// `.MuiSelect-select` element itself, so shrinking it without also hiding its overflow was what
		// let the text spill out from under a narrowed box and draw across the `8H` chip.
		flexShrink: 1,
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		"&.MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 },
		py: 0.25,
		px: 0.75,
		// Same rectangular radius as the `MetaChip`s beside it — a radius change alters no box dimension.
		borderRadius: CHIP_RADIUS,
		border: "1px solid transparent",
		backgroundColor: alpha(theme.palette.info.main, 0.12),
		color: theme.palette.info.dark,
		"& > .MuiSvgIcon-root": { display: "none" },
		"@media (hover: hover) and (pointer: fine)": {
			"&:hover": { borderColor: "transparent", backgroundColor: alpha(theme.palette.info.main, 0.2) },
		},
	}) as const;

/**
 * The row's two icon buttons, sized as MICRO controls.
 *
 * `iconButton` draws a `CONTROL_HEIGHT.COMPACT` (32px) box, which next to the `size="compact"` Apply
 * button made the two bare glyphs the heaviest things in the action cluster — a bordered 32px square
 * either side of the one control that carries a verb. `CONTROL_HEIGHT.MICRO` (24px) with the
 * `ICON_SIZE.MICRO` (15px) glyph is the app's existing pairing for an action that sits inside content
 * rather than in chrome (see `ChatActionButton`), and it applies here for the same reason.
 *
 * The HIT AREA is untouched: both buttons keep `touchTarget`, whose `TOUCH_TARGET_SX` is merged LAST
 * inside `SystemButton` — after `iconButtonSx` AND after this — so on a coarse pointer they still
 * measure `TOUCH_TARGET_MIN` (44px) however small the visual box gets. Which is also why
 * `SUGGESTED_HIRE.ROW_MIN_HEIGHT` (44 + 12) is unchanged by this: it is floored by the touch target,
 * not by the drawn box.
 *
 * Both buttons take the same size, so "smaller +" does not become "+ smaller than the trash beside
 * it" — the cluster reads as one row of controls at one scale.
 */
const MICRO_ICON_BUTTON_SX = {
	width: CONTROL_HEIGHT.MICRO,
	height: CONTROL_HEIGHT.MICRO,
	minWidth: CONTROL_HEIGHT.MICRO,
	"& .MuiSvgIcon-root": { fontSize: ICON_SIZE.MICRO },
} as const;

/** An outlined square icon button, so a bare glyph still reads as a control next to a filled button. */
const ICON_SQUARE_SX = { borderRadius: 1, border: `1px solid ${NEUTRAL[200]}` } as const;

/**
 * One suggested person on ONE line: their name, the shape of their week as a run of quiet pills, the
 * days themselves in their own aligned column, and the decisions (add as a recurring shift / apply /
 * remove).
 *
 * One line is the point — a list of 17 people is something you scan, and at three lines a row you
 * could read was a row you had to scroll past. What paid for it was deleting the employment-type
 * pill: the generator NAMES rows after the type ("Part Time 2", "Morning PT"), so the pill printed
 * the same fact twice and its wrap was the third line. The derived type is still live in the summary
 * strip beside the tabs, which is also the only place it stays accurate — see the note there.
 *
 * The name still leads (bold, largest on the line) and the pills recede to uppercase micro-chips, so
 * the run beside it annotates rather than competes.
 *
 * The day chips carry a still-short marker whose colour comes from the SAME tone function as the gaps
 * table's cells (`utils/gapTones`), so a chip can never claim a day is fine while the table shows it red.
 */
const SuggestedHireRow: FC<Props> = ({
	hire,
	displayName,
	gridWeeks,
	weekNumbers,
	weekStartDay,
	dates,
	shiftLabels,
	markerToneFor,
	selected,
	index,
	onSelectToggle,
	onHoverChange,
	onRename,
	onShiftsChange,
	onDayChange,
	onDayNeedsShift,
	pendingDayKey,
	retiredShifts,
	onApplyToggle,
	onRemove,
	onAddRecurring,
	"data-annie-target": annieTarget,
}) => {
	const { t } = useTranslation();
	const perWeek = shiftsByWeek(hire, gridWeeks);
	/**
	 * Frozen EXCEPT the two controls that are still meaningful: the shift picker, so you can re-point
	 * the row at whatever replaced the dead shift and keep the name you gave it, and Remove, so the row
	 * isn't stuck on the page until you Revert every other edit with it.
	 *
	 * Deliberately not re-pointed for you: the old `11pm-7am` and the new `7pm-7am` are different
	 * lengths, so moving someone across silently rewrites their week — an 8H part-timer becomes a 12H
	 * full-timer. Only the person reading the row knows whether that is the same job.
	 */
	const isRetired = retiredShifts.length > 0;
	const groups = buildDayGroups(hire, gridWeeks, weekNumbers, weekStartDay, dates, markerToneFor, t, pendingDayKey, isRetired);

	const handleDayToggle = (cell: DayToggleCell) => {
		const key = Number(cell.key);
		if (cell.selected) {
			onDayChange(key, null);
			return;
		}
		// One shift type → nothing to ask. Several → the caller opens the picker for this day.
		if (hire.coverShifts.length === 1) {
			onDayChange(key, hire.coverShifts[0]);
			return;
		}
		onDayNeedsShift(key);
	};

	return (
		<Box
			data-testid={`hiring-hire-row-${hire.id}`}
			data-annie-target={annieTarget}
			onMouseEnter={() => onHoverChange(true)}
			onMouseLeave={() => onHoverChange(false)}
			onClick={(event) => {
				// A click on a control inside the row belongs to that control, not to the row's pin toggle.
				if ((event.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) {
					return;
				}
				onSelectToggle();
			}}
			sx={(theme) => ({
				position: "relative",
				display: "grid",
				gridTemplateColumns: hireRowColumns(dayToggleStripWidth(groups)),
				alignItems: "center",
				columnGap: 2,
				rowGap: 1,
				minHeight: SUGGESTED_HIRE.ROW_MIN_HEIGHT,
				// The card lives in a flex column, where the default `flex-shrink: 1` squashes every item
				// toward its min-height — which once rendered the shift/hours pills UNDER the next card.
				// A list row is never compressible.
				flexShrink: 0,
				pl: 2,
				pr: 1.5,
				// 4px, down from 6px: with the action cluster's controls at 24-26px the row's height is now
				// its padding, and 12px of it on a 26px control read as a card with an empty band above and
				// below the one line it holds.
				py: 0.5,
				cursor: "pointer",
				borderRadius: 1.5,
				// A card per person, not a striped row: each hire is an object you edit and apply, and the
				// selected/applied state has to be legible on the object itself.
				border: `1px solid ${selected ? ACCENT_BLUE.MAIN : hire.applied ? alpha(theme.palette.success.main, 0.4) : NEUTRAL[200]}`,
				backgroundColor: selected ? ACCENT_BLUE.TINT : hire.applied ? alpha(theme.palette.success.main, 0.06) : SURFACE,
				// Left rail encodes the row's state at a glance down the list: applied, pinned, or neither.
				"&::before": {
					content: '""',
					position: "absolute",
					insetBlock: 0,
					left: 0,
					width: SUGGESTED_HIRE.ACCENT_RAIL,
					borderRadius: "inherit",
					backgroundColor: hire.applied ? theme.palette.success.main : selected ? ACCENT_BLUE.MAIN : "transparent",
					transition: theme.transitions.create("background-color", { duration: MOTION.DURATION.CONTROL }),
				},
				transition: theme.transitions.create(["background-color", "border-color"], { duration: MOTION.DURATION.CONTROL, easing: MOTION.EASE_OUT }),
				"@media (hover: hover) and (pointer: fine)": {
					"&:hover": {
						backgroundColor: hire.applied ? alpha(theme.palette.success.main, 0.1) : selected ? alpha(theme.palette.primary.main, 0.1) : NEUTRAL[50],
					},
				},
				"@keyframes hireRowEnter": {
					from: { opacity: 0, transform: "translateY(4px)" },
					to: { opacity: 1, transform: "translateY(0)" },
				},
				animation: `hireRowEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT} both`,
				animationDelay: `${Math.min(index * MOTION.STAGGER.STEP, MOTION.STAGGER.MAX)}ms`,
				"@media (prefers-reduced-motion: reduce)": { animation: "none", transition: "none" },
			})}
		>
			{/* Name and pills on ONE line. The name takes a fixed slice (`NAME_WIDTH`) and is the only thing
			    here allowed to ellipsize — it is the one string a user can rename and still recognise from
			    its start. From `lg` up the pill run neither wraps nor shrinks (`IDENTITY_WIDTH` is budgeted
			    from the pills' measured widths), which is what guarantees the row stays one line however
			    long someone renames a person. Below `lg` the whole row already stacks into a single column,
			    so wrapping stays ON there — a phone has no 478px identity column to lay this out in.
			    The shift-type picker stays interactive here. */}
			{/* `gap: 0.5` (4px), down from 8px: the pill run starts closer to the name it annotates, which is
			    what stops it reading as a second, detached column. 4px is enough separation here because
			    the type contrast does the rest of the work — a 14px/700 name against 11px uppercase
			    micro-chips. The 4px it gives back is spent on the inter-chip gap below. */}
			<Box sx={{ display: "flex", alignItems: "center", flexWrap: { xs: "wrap", lg: "nowrap" }, gap: 0.5, minWidth: 0 }}>
				<InlineEditableText
					value={displayName}
					disabled={isRetired}
					onCommit={onRename}
					ariaLabel={t("hiring.hire.row.nameLabel")}
					dataTestId={`hiring-hire-name-${hire.id}`}
					sx={{ width: SUGGESTED_HIRE.NAME_WIDTH, flexShrink: 0, fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700 }}
				/>
				{/* `overflow: hidden` is the containment for the ONE case the budget can't cover: a person the
				    user re-pointed at two shift types renders "12AM-8AM + 8AM-4PM", which is ~75px wider than
				    the column. Clipping it at the column edge keeps it out of the day strip's lane (the strips
				    are what the pinned columns exist to align); the generator itself only ever emits one
				    template per person, which is what the width budget is sized for. */}
				{/* `gap: 0.75` (6px), up from 4px: at 4px four chips of four different tones read as one
				    striped block, and the run's job is to be four separable facts. 6px is the whole spend —
				    see `SUGGESTED_HIRE.IDENTITY_WIDTH` for the arithmetic this and the 4px above land in. */}
				<Box sx={{ display: "flex", alignItems: "center", flexWrap: { xs: "wrap", lg: "nowrap" }, gap: 0.75, minWidth: 0, overflow: { lg: "hidden" } }}>
					{/* No JOB chip here. Every row in this panel is the job named in the page header's picker,
					    so a chip on each one repeated the same word down the whole column without ever
					    distinguishing two rows from each other. */}
					<SystemDropdown<string>
						multiple
						trigger="pill"
						// The label already names the selection, so the count badge would just be a redundant "1".
						hideTriggerCount
						// The schedule template's own hours, which is how the recurring-gaps grid is keyed — so
						// the pill and the table row are obviously the same shift.
						label={shiftPillLabel(hire.coverShifts)}
						// Reads as a flat uppercase chip that happens to be clickable, matching the pills beside it.
						selectSx={shiftPillSx}
						fieldLabel={t("hiring.hire.row.shiftsLabel")}
						items={shiftLabels.map((label) => ({ value: label, label: label }))}
						selectedItems={hire.coverShifts}
						minimalSelection={1}
						onChangeMultiple={(labels) => onShiftsChange(labels)}
						data-testid={`hiring-hire-shifts-${hire.id}`}
					/>
					<MetaChip label={t("hiring.hire.row.perWeek", { counts: perWeek.join("-") })} tooltip={t("hiring.hire.row.perWeekTooltip")} sx={UPPERCASE_CHIP_SX} />
					{/* Names the dead shift rather than just greying the row: without it the only signal is that
				    Apply stopped working, which reads as a broken page rather than a schedule that moved on. */}
					{isRetired && (
						<MetaChip
							label={t("hiring.hire.row.retired")}
							tone="warning"
							tooltip={t("hiring.hire.row.retiredTooltip", { shifts: retiredShifts.join(", ") })}
							sx={UPPERCASE_CHIP_SX}
							dataTestId={`hiring-hire-retired-${hire.id}`}
						/>
					)}
				</Box>
			</Box>

			<DayToggleGrid groups={groups} onToggle={handleDayToggle} tone={hire.applied ? "success" : "accent"} dataTestId={`hiring-hire-days-${hire.id}`} />

			<Box sx={{ display: "flex", alignItems: "center", justifyContent: { lg: "flex-end" }, gap: 0.5 }}>
				{/* The one action on this row that leaves the module: it hands the proposed week to the app's
				    real Recurring Shift modal, pre-filled, for whoever actually got hired. This slot used to
				    hold a "show this person in the grid" toggle, which the row's own click already does. */}
				<SystemButton
					buttonVariant="secondary"
					iconButton
					touchTarget
					aria-label={t("hiring.hire.row.addRecurring")}
					disabled={isRetired}
					tooltip={isRetired ? t("hiring.hire.row.retiredTooltip", { shifts: retiredShifts.join(", ") }) : t("hiring.hire.row.addRecurring")}
					onClick={onAddRecurring}
					data-testid={`hiring-hire-add-recurring-${hire.id}`}
					sx={(theme) => ({
						...ICON_SQUARE_SX,
						...MICRO_ICON_BUTTON_SX,
						color: NEUTRAL[500],
						backgroundColor: "transparent",
						transition: theme.transitions.create(["color", "background-color", "border-color"], { duration: MOTION.DURATION.CONTROL }),
						"@media (hover: hover) and (pointer: fine)": {
							"&:hover": { color: ACCENT_BLUE.INK, borderColor: ACCENT_BLUE.MAIN, backgroundColor: ACCENT_BLUE.TINT },
						},
						"@media (prefers-reduced-motion: reduce)": { transition: "none" },
					})}
				>
					<AddIcon />
				</SystemButton>
				{hire.applied ? (
					<MetaChip
						label={t("hiring.hire.row.applied")}
						tone="success"
						icon={<CheckIcon />}
						onClick={onApplyToggle}
						ariaLabel={t("hiring.hire.row.unapply")}
						tooltip={t("hiring.hire.row.unapply")}
						dataTestId={`hiring-hire-applied-${hire.id}`}
						sx={{ minHeight: 26, px: 0.75 }}
					/>
				) : (
					// Borderless on purpose: a column of 17 outlined buttons was the loudest thing on the panel,
					// competing with the day strips that carry the actual information. The APPLIED state is the
					// one that earns weight (the success chip above), not the invitation.
					<SystemButton
						buttonVariant="primary"
						size="compact"
						noFullWidthMobile
						leadingIcon={<CheckIcon sx={{ fontSize: 14 }} />}
						leadingIconGap="6px"
						onClick={onApplyToggle}
						disabled={isRetired}
						tooltip={isRetired ? t("hiring.hire.row.retiredTooltip", { shifts: retiredShifts.join(", ") }) : t("hiring.hire.row.applyTooltip")}
						/**
						 * The design specifies this blue; `ACCENT_BLUE` is fixed rather than brand-derived.
						 *
						 * Sized to the `Applied` chip it toggles into (26px, `px: 0.75`) rather than to
						 * `size="compact"`'s 32px box: the two are the same control in two states, and a
						 * height change between them made the whole row jump on every Apply. It is also the
						 * tallest thing on the line once the icon buttons went MICRO, so this is what let
						 * `ROW_MIN_HEIGHT` come down.
						 */
						sx={{
							minHeight: 26,
							px: 0.75,
							fontSize: FONT_SIZE.LABEL_XS,
							backgroundColor: ACCENT_BLUE.MAIN,
							color: ACCENT_BLUE.ON_ACCENT,
							"&:hover": { backgroundColor: ACCENT_BLUE.DARK },
						}}
						data-testid={`hiring-hire-apply-${hire.id}`}
					>
						{t("hiring.hire.row.apply")}
					</SystemButton>
				)}
				<SystemButton
					buttonVariant="borderless"
					iconButton
					touchTarget
					aria-label={t("hiring.hire.row.remove")}
					tooltip={t("hiring.hire.row.remove")}
					onClick={onRemove}
					data-testid={`hiring-hire-remove-${hire.id}`}
					sx={{ ...MICRO_ICON_BUTTON_SX, color: NEUTRAL[500] }}
				>
					<DeleteOutlineIcon />
				</SystemButton>
			</Box>
		</Box>
	);
};

export default SuggestedHireRow;
